import { IDataPackageFileDescriptor, IUploader } from '@vivli/shared/features/data-package/infrastructure/interface';
import { CSSProperties, useMemo } from 'react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { DropdownFieldComponent, UploadProgressBarComponent } from '@vivli/shared/components';
import moment from 'moment';
import { GridCellRendererEnum } from '@vivli/shared/infrastructure/enum';
import { Color } from '@vivli/shared/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEyeSlash, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { ButtonView, GridView } from '@vivli/shared/views';
import { DTIAMRDropDown } from '@vivli/shared/infrastructure/constants';
import { isFileUploadFailed, isFileUploadInProgress } from '@vivli/shared/infrastructure/hook';
import { useTranslation } from 'react-i18next';
import { useVivliConfigContext } from '@vivli/core/infrastructure/context';

const fileTypeStyle: CSSProperties = {
    width: '100%',
    marginRight: '10px',
    minWidth: '160px',
    paddingTop: '0px',
    marginTop: '0px',
    padding: '5px',
    backgroundColor: 'transparent'
};

const fileTypeDivStyle: CSSProperties = {
    backgroundColor: 'initial',
    gridTemplateColumns: 'auto',
    boxShadow: 'none'
};

const btnStyle: CSSProperties = {
    padding: 0,
    minWidth: '45px',
    width: '45px'
};

const customContainerStyle: CSSProperties = {
    display: 'grid',
    placeContent: 'center end'
};

const btnContainerStyle = (show2ndCol?: boolean): CSSProperties => ({
    display: 'grid',
    gridTemplateColumns: show2ndCol ? 'auto auto' : 'auto',
    columnGap: '10px'
});

const uploadControlsContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    alignItems: 'center',
    columnGap: '8px',
    justifyContent: 'end'
};

const failedUploadControlsContainerStyle = (showRetry: boolean): CSSProperties => ({
    display: 'grid',
    gridTemplateColumns: showRetry ? 'auto auto auto' : 'auto auto',
    alignItems: 'center',
    columnGap: '8px',
    justifyContent: 'end'
});

const failedMessageStyle: CSSProperties = {
    color: Color.ERROR_RED,
    fontWeight: 600,
    whiteSpace: 'nowrap'
};

const retryButtonStyle: CSSProperties = {
    minWidth: '70px',
    width: '70px'
};

// Every upload starts with this sentinel type (use-data-package.hook.ts), so a read-only grid must
// render it as an empty cell rather than the literal word. (DEV-486, folded in from DEV-484)
const UNKNOWN_FILE_TYPE = 'Unknown';

// The Uploaded Date cell shows the date alone to keep the column narrow; the full timestamp moves to
// the cell tooltip so the exact upload time is still one hover away. (DEV-482)
// Format is fixed by the acceptance criteria as 01-Jan-2021: zero-padded day, abbreviated month. (DEV-484)
const UPLOAD_DATE_FORMAT = 'DD-MMM-YYYY';
const UPLOAD_DATE_TIME_FORMAT = 'DD-MMM-YYYY h:mm:ss a';

// Guard the empty case: moment(undefined) resolves to "now", so a descriptor with no
// updatedDateTime used to render today's date instead of a blank cell.
const formatUploadDate = (value: string, format: string) => (value ? moment(value).local().format(format) : '');

const typeDropdownStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'grid',
    alignContent: 'center',
    textAlign: 'left',
    lineHeight: 1
};

interface UploaderListViewProps extends IUploader {
    files?: IDataPackageFileDescriptor[];
    onFileDeleted?: (file: IDataPackageFileDescriptor) => void;
    scrollEnabled?: boolean;
    hybridMode?: boolean;
    secondaryFilesToShow?: IDataPackageFileDescriptor[];
}

export const UploaderListView = (props: UploaderListViewProps) => {
    const {
        files,
        readonly,
        showExpandedFilename,
        isLoading,
        availableTypes,
        showFileType,
        hideUploadDate,
        hideUploadedBy,
        allowDelete,
        allowOwnerDelete,
        allowDownload,
        onFileDeleted,
        onFileDownload,
        onFileTypeChanged,
        onPubliclyAvailableChanged,
        user,
        allowUpload,
        miniature,
        showPubliclyAvailable,
        rowHeight = 55,
        hideForPubliclyAvailableTypes,
        hideSize,
        allowPubliclyAvailableEdit,
        showOrgName,
        usePagination,
        pageSize,
        hybridMode,
        onFileUploadCancelled,
        onFileUploadRetry,
        subscribeFileProgress,
        isFilePending
    } = props;

    const { t } = useTranslation();
    const vivliConfig = useVivliConfigContext();

    // DEV-486: the read-only File Type cell shows the configured display name instead of the raw
    // stored code, so a researcher sees "Data Dictionary" rather than the internal value.
    const fileTypeDisplayNames = useMemo(() => {
        const displayNames = new Map<string, string>();
        vivliConfig?.ipdFileTypes?.forEach((ipdFileType) => {
            if (ipdFileType.fileType) {
                displayNames.set(ipdFileType.fileType, ipdFileType.displayName || ipdFileType.fileType);
            }
        });
        return displayNames;
    }, [vivliConfig?.ipdFileTypes]);

    const getFileTypeLabel = (type: string) => {
        if (!type || type === UNKNOWN_FILE_TYPE) {
            return '';
        }

        const displayName = fileTypeDisplayNames.get(type);
        if (!displayName) {
            // config has no entry for a stored type: show the raw value rather than hide the file
            return type;
        }

        return t(displayName);
    };

    const getDisplaySize = (file: IDataPackageFileDescriptor) => {
        if (!file) {
            return '';
        }

        let { sizeInKb } = file;
        let displaySize = '';

        // 0 is equal to false so specifically checking for null or undefined
        if (sizeInKb === undefined || sizeInKb === null) {
            return displaySize;
        }

        if (sizeInKb > 1024) {
            sizeInKb = sizeInKb / 1024;
            displaySize = `${parseFloat(sizeInKb.toString()).toFixed(2)}MB`;
        } else if (sizeInKb <= 0) {
            displaySize = '< 1 kB';
        } else {
            displaySize = `${parseFloat(sizeInKb.toString()).toFixed(2)}kB`;
        }

        return displaySize;
    };

    const TypeDropdownView = ({ data, node }: ICellRendererParams<IDataPackageFileDescriptor>) => {
        const isLinkedSharedFile = data.publicAccessGranted && !showPubliclyAvailable;
        const rowDisabled = isFilePending ? isFilePending(data.name) : false;

        // Deliberately NOT locked while the upload is in progress. `readonly` on this component renders
        // plain text rather than a disabled control, so locking a transferring row replaced its selector
        // with an empty div — and with maxSimultaneousUploads:3 that is the last file of a multi-file
        // upload, which is what "the last row has no File Type selector" was. The write path never needed
        // the guard: useDataPackage merges each change onto the freshest descriptor when the queued write
        // is dequeued, so assigning a type mid-upload cannot clobber the completion write. (DEV-711)
        return (
            <DropdownFieldComponent
                placement="auto"
                style={fileTypeStyle}
                divStyle={fileTypeDivStyle}
                iconStyle={{ bottom: '8' }}
                readonly={readonly || rowDisabled || isLinkedSharedFile}
                // CONTROLLED. With defaultValue the component only resolved its selection on mount, so
                // the cell showed the right type purely because ag-grid used to destroy and recreate it
                // on every render; once that stopped, a blanked cell stayed blank forever. data.type is
                // always set ('Unknown' from upload start), so a controlled value always resolves.
                value={data.type}
                items={availableTypes?.map((t) => ({
                    title: t,
                    value: t
                }))}
                onChange={(newFileType) => {
                    onFileTypeChanged && onFileTypeChanged(data, newFileType as string);

                    // update the grid so it hides / shows publicly available checkboxes as needed
                    node.updateData({
                        ...data,
                        type: newFileType as string
                    });
                }}
                // error={fieldHasError()}
                hidePopoverError={true}
                dataId={DTIAMRDropDown.DropdownMenu('value')}
            />
        );
    };

    const RowControlsView = (file: IDataPackageFileDescriptor) => {
        const isOwner = user?.id === file.uploadedByUserId;
        const showDeleteBtn = allowDelete || (allowOwnerDelete && isOwner);
        const rowDisabled = isFilePending ? isFilePending(file.name) : false;

        return (
            <div style={btnContainerStyle(showDeleteBtn)}>
                {showDeleteBtn && (
                    <ButtonView
                        title={hybridMode && file.publicAccessGranted ? 'Remove File' : 'Delete File'}
                        style={btnStyle}
                        onClick={() => onFileDeleted(file)}
                        theme={'red-on-white'}
                        disabled={rowDisabled}
                        isLoading={rowDisabled}
                    >
                        <FontAwesomeIcon
                            style={{ cursor: 'pointer', color: Color.ERROR_RED }}
                            icon={hybridMode && file.publicAccessGranted ? faEyeSlash : faTrash}
                        />
                    </ButtonView>
                )}
                {allowDownload && (
                    <ButtonView title="Download File" style={btnStyle} onClick={() => onFileDownload(file)} disabled={rowDisabled}>
                        <FontAwesomeIcon style={{ cursor: 'pointer', color: 'white' }} icon={faDownload} />
                    </ButtonView>
                )}
            </div>
        );
    };

    const UploadControlsView = (file: IDataPackageFileDescriptor) => {
        return (
            <div style={uploadControlsContainerStyle}>
                <UploadProgressBarComponent uploaderId={file.uploaderId} subscribeFileProgress={subscribeFileProgress} />
                <ButtonView
                    title="Cancel Upload"
                    style={btnStyle}
                    onClick={() => onFileUploadCancelled && onFileUploadCancelled(file)}
                    theme={'red-on-white'}
                    dataId={`file-cancel-${file.name}`}
                >
                    <FontAwesomeIcon style={{ cursor: 'pointer', color: Color.ERROR_RED }} icon={faXmark} />
                </ButtonView>
            </div>
        );
    };

    const FailedUploadControlsView = (file: IDataPackageFileDescriptor) => {
        // Retry only works in the session that performed the upload: useFileUploader retains the
        // original File keyed by uploaderId, and retry is a no-op once that File is gone. After a
        // navigate-away / reopen the retained File is lost and uploaderId (client-only) is absent on
        // the reloaded descriptor, so only Delete is offered there.
        const canRetry = !!file.uploaderId;

        return (
            <div style={failedUploadControlsContainerStyle(canRetry)}>
                <div style={failedMessageStyle}>Failed</div>
                {canRetry && (
                    <ButtonView title="Retry Upload" style={retryButtonStyle} onClick={() => onFileUploadRetry && onFileUploadRetry(file)}>
                        Retry
                    </ButtonView>
                )}
                <ButtonView
                    title="Delete File"
                    style={btnStyle}
                    onClick={() => onFileUploadCancelled && onFileUploadCancelled(file, `File removed: ${file.name}`)}
                    theme={'red-on-white'}
                    dataId={`file-delete-failed-${file.name}`}
                >
                    <FontAwesomeIcon style={{ cursor: 'pointer', color: Color.ERROR_RED }} icon={faTrash} />
                </ButtonView>
            </div>
        );
    };

    const gridCols: ColDef<IDataPackageFileDescriptor>[] = [
        {
            headerName: 'File Name',
            field: 'name',
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: ({ data, node }) => ({
                dataId: `file-${node.rowIndex}-name-${data.name}`,
                scrollable: false,
                centered: false,
                truncate: true
            }),
            valueGetter: ({ data }) => {
                const filenameText = showExpandedFilename
                    ? data.name +
                      ' - Uploaded: ' +
                      moment(data.updatedDateTime).format('M-D-YY h:mm a') +
                      ', Uploaded by: ' +
                      data.uploadedByUserEmail
                    : data.name;
                return filenameText;
            },
            minWidth: miniature ? 150 : 300
        },
        {
            headerName: 'Size',
            field: 'sizeInKb',
            valueFormatter: ({ data }) => getDisplaySize(data),
            maxWidth: 100,
            minWidth: 100,
            hide: hideSize || showExpandedFilename,
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: (params: ICellRendererParams) => ({
                component: <div>{params.valueFormatted}</div>,
                dataId: `file-${params.node.rowIndex}-size-${params.valueFormatted}`
            })
        },
        {
            headerName: 'Org Name',
            field: 'organizationName',
            maxWidth: 200,
            minWidth: 100,
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: ({ data, node }) => ({
                dataId: `file-${node.rowIndex}-org-name`
            }),
            hide: !showOrgName
        },
        {
            headerName: 'Uploaded By',
            field: 'uploadedByUserName',
            maxWidth: 200,
            minWidth: 100,
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: ({ data, node }) => ({
                dataId: `file-${node.rowIndex}-uploaded-by-${data.uploadedByUserName}`,
                scrollable: false
            }),
            // DEV-486 opts the researcher-facing download screens out of this column
            hide: showExpandedFilename || hideUploadedBy
        },
        {
            headerName: 'File Type',
            field: 'type',
            minWidth: 350,
            maxWidth: 350,
            sortable: false,
            // availableTypes guard kept alongside showFileType so a call site that opts in without
            // supplying types can't render an empty dropdown.
            hide: !showFileType || availableTypes?.length === 0 || readonly,
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: (params: ICellRendererParams) => ({
                component: TypeDropdownView(params),
                containerStyle: typeDropdownStyle,
                dataId: `file-${params.node.rowIndex}-type-${params.data.type}`
            })
        },
        {
            headerName: 'File Type',
            field: 'type',
            hide: !showFileType || !readonly,
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: (params: ICellRendererParams) => ({
                component: <div>{getFileTypeLabel(params.data.type)}</div>,
                dataId: `file-${params.node.rowIndex}-type-${params.data.type}`
            })
        },
        {
            headerName: 'Publicly Available',
            field: 'publicAccessGranted',
            // Deliberately independent of showFileType. The old hideFileType flag suppressed this column
            // too, so a context could not hide the type selector while keeping the checkbox. (DEV-481)
            hide: !showPubliclyAvailable,
            maxWidth: 150,
            minWidth: 150,
            filter: false,
            cellRenderer: GridCellRendererEnum.GridCheckbox,
            cellRendererParams: ({ data, node }) => ({
                style: { height: '20px', width: '20px' },
                onChange: (checked: boolean, file: IDataPackageFileDescriptor) => {
                    onPubliclyAvailableChanged(file, checked);
                    return true;
                },
                hide: hideForPubliclyAvailableTypes?.some((type) => type === data.type),
                readonly: (readonly && !allowPubliclyAvailableEdit) || (isFilePending ? isFilePending(data.name) : false),
                dataId: `file-${node.rowIndex}-publicly-available-${data.publicAccessGranted ? 'true' : 'false'}`
            })
        },
        {
            headerName: 'Uploaded Date',
            field: 'updatedDateTime',
            // Shown by default: RQ-148 requires name / size / upload date / uploader on every
            // uploader, so this is opt-out rather than opt-in. (DEV-482)
            hide: hideUploadDate,
            minWidth: 150,
            maxWidth: 200,
            // GridCellLongText (not GridCustomDisplay) because its HelpTextView popover is what actually
            // renders on hover in this grid — see the Uploaded By column. ag-grid's own tooltipValueGetter
            // does nothing here: GridView never enables ag-grid tooltips.
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: ({ data, node }) => ({
                dataId: `file-${node.rowIndex}-uploaded-date-${data.updatedDateTime}`,
                // the cell shows the date alone; the popover carries the full timestamp
                titleText: formatUploadDate(data.updatedDateTime, UPLOAD_DATE_TIME_FORMAT),
                scrollable: false,
                centered: false
            }),
            valueFormatter: ({ value }) => formatUploadDate(value, UPLOAD_DATE_FORMAT)
        },
        {
            headerName: '',
            hide: !allowDownload && !allowOwnerDelete && !allowDelete && !allowUpload,
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: ({ data }) => {
                const showFailed = isFileUploadFailed(data);
                // Show the progress/cancel control until the upload is fully complete.
                // We key off isComplete only (set by onFileUploadCompleted after the server
                // confirms). Relying on percentUploaded === 100 here would switch to the row
                // controls as soon as the bytes finished transferring, i.e. before the upload
                // actually finished.
                // These are the same predicates the required-file-type validation uses, so the
                // Submit button can never be enabled while a row still shows Uploading or Failed.
                const showUploading = isFileUploadInProgress(data);
                // Delete/download (RowControlsView) only appear once a file has
                // successfully uploaded; while uploading we show the progress +
                // cancel control instead.
                return {
                    containerStyle: customContainerStyle,
                    component: showFailed
                        ? FailedUploadControlsView(data)
                        : showUploading
                          ? UploadControlsView(data)
                          : RowControlsView(data)
                };
            },
            maxWidth: 220,
            minWidth: 220,
            suppressMovable: true,
            sortable: false,
            filter: false,
            floatingFilter: false
        }
    ];

    const grid = (
        <GridView
            rowData={files}
            columns={gridCols}
            hideHeader={false}
            hideFloatingFilter={false}
            fullWidthColumns={true}
            emptyMessage={'Upload Files Here'}
            autoHeight={true}
            rowHeight={rowHeight}
            isLoading={isLoading}
            pagination={usePagination}
            pageSize={pageSize}
            getRowId={(params) => {
                return params.data.name;
            }}
        />
    );

    return <div>{grid}</div>;
};
